import React from "react";
import "./UiDropdown.css";
import { useState, useRef, useEffect } from "react";
import { ICON } from "../../../assets";
import UiIcon from "../UiIcon";

function UiDropdown({
  options = [],
  onSelect = () => {},
  style,
  dropdownStyle,
  optionStyle,
  className,
  errorMessage,
  placeholder,
  icon = ICON.errorIcon,
  throwError = false,
  required = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const [error, setError] = useState(null);
  const [waitforclick, setWaitforclick] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options || []);

  useEffect(() => {
    if (
      !selectedOption &&
      waitforclick &&
      errorMessage &&
      required &&
      throwError
    ) {
      setError(errorMessage);
    }
    if (throwError && !selectedOption && required) {
      setError(errorMessage || "Ce champ est obligatoire");
    }
  }, [selectedOption, waitforclick, errorMessage, throwError, required]);

  const toggleDropdown = () => {
    setWaitforclick(true);
    if (onSelect) onSelect(null);
    if (selectedOption) setSelectedOption(null);
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option) => {
    setInputValue(option.label);
    setSelectedOption(option);
    setIsOpen(false);
    if (onSelect) onSelect(option);
    if (option) setError(null);
  };

  const handleInputChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
    const value = e.target.value.toLowerCase();
    const shortList = options?.filter((opt) =>
      opt.label.toLowerCase().includes(value)
    );
    setInputValue(e.target.value);
    if (shortList.map((e) => e.label.toLowerCase()).includes(value)) {
      setSelectedOption(
        shortList.find((opt) => opt.label.toLowerCase() === value)
      );
    }
    if (shortList.find((opt) => opt.label.toLowerCase() === value)) {
      onSelect(shortList.find((opt) => opt.label.toLowerCase() === value));
      setError(null);
    } else {
      setError("Veuillez sélectionner une option dans la liste.");
      onSelect(null);
      setSelectedOption(null);
    }
    if (value !== "" && shortList.length === 0) {
      setError(
        errorMessage || "Veuillez sélectionner une option dans la liste."
      );
    } else {
      setError(null);
    }
    setFilteredOptions(shortList);
  };

  // Close dropdown when clicking outside - enabled for now
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const selectedOptionElement = dropdownRef.current?.querySelector(
      ".ui-dropdown-option.selected"
    );
    if (selectedOptionElement) {
      selectedOptionElement.scrollIntoView({
        block: "nearest",
        inline: "start",
      });
    }
  }, [selectedOption]);

  useEffect(() => {
    if (isOpen && filteredOptions.length !== 0) {
      setError(null);
    }
    function handleKeyDown(event) {
      if (isOpen) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          if (filteredOptions.length === 0) return;
          const currentIndex = filteredOptions.findIndex(
            (opt) => opt.value === selectedOption?.value
          );
          const nextIndex =
            currentIndex >= filteredOptions.length - 1 ? 0 : currentIndex + 1;
          const nextOption = filteredOptions[nextIndex];
          setSelectedOption(nextOption);
          setInputValue(nextOption.label);
          if (onSelect) onSelect(nextOption);
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          if (filteredOptions.length === 0) return;
          const currentIndex = filteredOptions.findIndex(
            (opt) => opt.value === selectedOption?.value
          );
          const prevIndex =
            currentIndex <= 0 ? filteredOptions.length - 1 : currentIndex - 1;
          const prevOption = filteredOptions[prevIndex];
          setSelectedOption(prevOption);
          setInputValue(prevOption.label);
          if (onSelect) onSelect(prevOption);
        } else if (event.key === "Enter") {
          event.preventDefault();
          setIsOpen(false);
          if (selectedOption) {
            setInputValue(selectedOption.label);
            if (onSelect) onSelect(selectedOption);
            setError(null);
          } else {
            setError(errorMessage || "Ce champ est obligatoire");
            if (onSelect) onSelect(null);
          }
        } else if (event.key === "Escape") {
          event.preventDefault();
          setIsOpen(false);
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    isOpen,
    filteredOptions,
    selectedOption,
    onSelect,
    errorMessage,
    setIsOpen,
    setSelectedOption,
    setInputValue,
    setError,
  ]);

  return (
    <React.Fragment>
      <div
        className={`ui-dropdown ${className || ""} ${error ? "error" : ""}`}
        style={{ position: "relative", ...style }}
        ref={dropdownRef}
      >
        <div
          className={`ui-dropdown-header ${error ? "error" : ""}`}
          onClick={toggleDropdown}
          style={dropdownStyle}
        >
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            className="ui-dropdown-input"
            placeholder={placeholder || "Sélectionnez dans la liste..."}
          />
          <span className="dropdown-arrow">
            <UiIcon
              size="md"
              icon={isOpen ? ICON.arrowUpIcon : ICON.arrowDownIcon}
              alt="arrow"
            />
          </span>
        </div>
        {isOpen && filteredOptions.length > 0 && (
          <div
            className={`ui-dropdown-options ${error ? "border-none" : ""}`}
            style={dropdownStyle}
          >
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={`ui-dropdown-option ${
                  selectedOption?.value === option.value ? "selected" : ""
                }`}
                onClick={() => handleOptionClick(option)}
                style={optionStyle}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
        {error && (
          <div className="ui-dropdown-error">
            {icon && <UiIcon icon={icon} alt="error icon" size="md" />}
            <span>{error}</span>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

export default UiDropdown;
